import { useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { getStatusColor, PRIMARY_COLOR } from './StatsHelpers';
import { StarIcon, FaceSmileIcon, ClockIcon } from '@heroicons/react/24/solid';

const CHART_TABS = [
  { key: 'breakdown', label: 'Completion Rate' },
  { key: 'distribution', label: 'Status' },
];

const LEGEND_COL_SIZE = 4;

const LegendGrid = ({ items }) => (
  <div className="tw-flex tw-gap-x-4 tw-items-start" style={{ flexFlow: 'column wrap', maxHeight: LEGEND_COL_SIZE * 28 }}>
    {items.map((item, i) => (
      <span key={i} className="tw-text-sm tw-flex tw-items-center tw-gap-1.5" style={{ height: 28 }}>
        <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-shrink-0">
          {item.icon
            ? item.icon
            : <span className="tw-inline-block tw-w-3 tw-h-3 tw-rounded-full" style={{ backgroundColor: item.color }} />
          }
        </span>
        {item.label} <span className="tw-font-semibold">{item.display}</span>
      </span>
    ))}
  </div>
);

const SummaryDoughnut = ({ summary, title, type }) => {
  const [activeTab, setActiveTab] = useState('breakdown');

  if (!summary || !summary.status_breakdown) return null;

  const isTask = /task/i.test(type);
  const showPct = /goal|review|task|event/i.test(type);
  const overdueEntry = summary.status_breakdown.find(s => /overdue/i.test(s.label)) || null;
  const chartBreakdown = overdueEntry
    ? summary.status_breakdown.filter(s => !/overdue/i.test(s.label))
    : summary.status_breakdown;

  const statusDist = summary.status_distribution;
  const distEntries = statusDist
    ? Object.entries(statusDist).map(([label, value]) => ({ label, value }))
    : [];
  const distTotal = distEntries.reduce((sum, e) => sum + e.value, 0);

  const hasTabs = isTask && distEntries.length > 0;

  const renderBreakdownChart = () => (
    <div className="tw-flex tw-items-center tw-gap-3">
      <div style={{ width: 150, height: 150, position: 'relative' }}>
        <Doughnut
          data={{
            labels: chartBreakdown.map(s => s.label),
            datasets: [{
              data: chartBreakdown.map(s => s.value),
              backgroundColor: chartBreakdown.map((s, i) => getStatusColor(s.label, i)),
              borderWidth: 1,
            }]
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            cutout: '60%',
            plugins: {
              legend: { display: false },
              tooltip: { bodyFont: { size: 11 } },
            },
          }}
          plugins={[{
            id: 'centerText',
            beforeDraw(chart) {
              const { ctx, width, height } = chart;
              ctx.save();
              ctx.font = 'bold 24px sans-serif';
              ctx.fillStyle = '#1f2937';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'bottom';
              ctx.fillText(summary.total_count, width / 2, height / 2 + 2);
              ctx.font = '600 10px sans-serif';
              ctx.fillStyle = '#6b7280';
              ctx.textBaseline = 'top';
              ctx.fillText(title.toUpperCase(), width / 2, height / 2 + 6);
              ctx.restore();
            }
          }]}
        />
      </div>
      <LegendGrid items={[
        ...(summary.avg_score != null && summary.avg_score_label != null ? [{
          icon: /sentiment/i.test(summary.avg_score_label)
            ? <FaceSmileIcon className="tw-w-4 tw-h-4" style={{ color: '#facc15' }} />
            : /rating/i.test(summary.avg_score_label)
              ? <StarIcon className="tw-w-4 tw-h-4" style={{ color: '#facc15' }} />
              : /day|time|duration/i.test(summary.avg_score_label)
                ? <ClockIcon className="tw-w-4 tw-h-4" style={{ color: '#3b82f6' }} />
                : null,
          label: `Avg ${summary.avg_score_label}:`,
          display: summary.avg_score.toFixed(1),
        }] : []),
        ...chartBreakdown.map((s, i) => ({
          color: getStatusColor(s.label, i),
          label: s.label,
          display: `${s.value}${showPct ? '%' : ''}`,
        })),
      ]} />
    </div>
  );

  const renderDistributionChart = () => (
    <div className="tw-flex tw-items-center tw-gap-3">
      <div style={{ width: 150, height: 150, position: 'relative' }}>
        <Doughnut
          data={{
            labels: distEntries.map(e => e.label),
            datasets: [{
              data: distEntries.map(e => e.value),
              backgroundColor: distEntries.map((e, i) => getStatusColor(e.label, i)),
              borderWidth: 1,
            }]
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            cutout: '60%',
            plugins: {
              legend: { display: false },
              tooltip: { bodyFont: { size: 11 } },
            },
          }}
          plugins={[{
            id: 'distCenterText',
            beforeDraw(chart) {
              const { ctx, width, height } = chart;
              ctx.save();
              ctx.font = 'bold 24px sans-serif';
              ctx.fillStyle = '#1f2937';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'bottom';
              ctx.fillText(distTotal, width / 2, height / 2 + 2);
              ctx.font = '600 10px sans-serif';
              ctx.fillStyle = '#6b7280';
              ctx.textBaseline = 'top';
              ctx.fillText('BY STATUS', width / 2, height / 2 + 6);
              ctx.restore();
            }
          }]}
        />
      </div>
      <LegendGrid items={distEntries.map((e, i) => ({
        color: getStatusColor(e.label, i),
        label: e.label,
        display: `${e.value}`,
      }))} />
    </div>
  );

  // Tasks type: tabbed single-chart view
  if (hasTabs) {
    return (
      <div className="tw-mb-3">
        <div className="tw-flex tw-gap-4 tw-mb-3 tw-border-0 tw-border-b tw-border-solid tw-border-gray-200">
          {CHART_TABS.map(tab => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`tw-px-1 tw-pb-2 tw-bg-transparent tw-border-0 tw-border-b-2 tw-border-solid tw-text-sm tw-cursor-pointer tw-transition-colors ${
                  isActive
                    ? 'tw-font-semibold'
                    : 'tw-border-transparent tw-text-gray-400 hover:tw-text-gray-600'
                }`}
                style={isActive ? { color: PRIMARY_COLOR, borderBottomColor: PRIMARY_COLOR } : {}}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
          {activeTab === 'breakdown' ? renderBreakdownChart() : renderDistributionChart()}
          {overdueEntry && (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-gray-50 tw-rounded-lg tw-px-5 tw-py-3 tw-ml-2">
              <span className="tw-text-2xl tw-font-bold" style={{ color: '#ef4444' }}>{overdueEntry.value}%</span>
              <span className="tw-text-xs tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wide tw-mt-1">Overdue {title}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default: side-by-side for non-task types
  return (
    <div className="tw-flex tw-items-center tw-gap-3 tw-mb-3 tw-flex-wrap">
      {renderBreakdownChart()}

      {distEntries.length > 0 && renderDistributionChart()}

      {overdueEntry && (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-gray-50 tw-rounded-lg tw-px-5 tw-py-3 tw-ml-2">
          <span className="tw-text-2xl tw-font-bold" style={{ color: '#ef4444' }}>{overdueEntry.value}%</span>
          <span className="tw-text-xs tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wide tw-mt-1">Overdue {title}</span>
        </div>
      )}
    </div>
  );
};

export default SummaryDoughnut;
